import {
  buildSummaryListEffectiveLineItems,
  getDownstreamIncludedOptionGroups,
  getSummaryGroupsEffectivePriceInCents,
} from "src/utils/summary.utils";
import { SummaryUtils_CheckoutConfigOptionGroupFragment } from "src/utils/summary.utils.generated";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";

import { currencyFormatter } from "~utils";
import { SummaryPrintConfiguration_CheckoutConfigFragment } from "../SummaryPrint.page.generated";
import { ExteriorPDFCard } from "./Exterior.report";
import { FloorPlanPDFCard } from "./FloorPlan.report";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";
import { PrintListItem } from "./PrintListItem";

function hasSelectedOption(ccog: SummaryUtils_CheckoutConfigOptionGroupFragment) {
  return !!ccog.selectedOption;
}

type SummaryReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
};

export function SummaryReport({ data, shouldDisplayPricing }: SummaryReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail, optionGroups, totalPriceInCents } = data ?? {};

  const sortedOptionGroups = optionGroups.sortBy((ccog) => ccog.group.location?.name);

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <PrintHeader
            status={status}
            expiredAt={expiresAt}
            createdAt={createdAt}
            reservedAt={reservedAt}
            configurationName={name}
            customerEmail={customerEmail}
          />
          {/* Customer and Build section */}
          <FloorPlanPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} />
          <ExteriorPDFCard optionGroups={sortedOptionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <StructuralOptionsPDFCard optionGroups={sortedOptionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <InteriorPDFCard optionGroups={sortedOptionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          {/* Filters out any option groups that have been enabled by the spec level */}
          <InteriorPersonalizationAndAddOnsPDFCard
            optionGroups={sortedOptionGroups}
            shouldDisplayPricing={shouldDisplayPricing}
          />
          <AduPDFCard optionGroups={sortedOptionGroups} shouldDisplayPricing={shouldDisplayPricing} />
          <div css={Css.add({ breakInside: "avoid-page" }).$}>
            {/* Plan Cost and Estimated Total Cost */}
            {shouldDisplayPricing && (
              <div css={Css.df.fdc.aife.ptPx(12).add({ breakInside: "avoid-page" }).$}>
                <div css={Css.df.colGap1.$}>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>
                    <sup>*</sup>Est. Total Cost
                  </h6>
                  <h6 css={Css.t18.fsPx(20).fw500.lhPx(32).gap1.$}>{currencyFormatter(totalPriceInCents)}</h6>
                </div>
              </div>
            )}
            {/* Disclaimer */}
            <div id="disclaimer" css={Css.t12.pt2.tac.gray700.$}>
              <sup>*</sup>Estimated total cost includes site-specific conditions that may vary based on lot conditions.
              All options and prices will be confirmed by a Homebound representative prior to final signature.
            </div>
          </div>
        </div>
      </div>
    </PDFPage>
  );
}

type StructuralOptionsPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function StructuralOptionsPDFCard({ optionGroups, shouldDisplayPricing }: StructuralOptionsPDFCardProps) {
  const floorPlanOptions = optionGroups.filter(
    (ccog) => ccog.step === CheckoutConfigOptionGroupStep.OptionsStructural && hasSelectedOption(ccog),
  );
  return (
    <PrintListItem
      title="Floor Plan Options"
      shouldDisplayPricing={shouldDisplayPricing}
      priceInCents={getSummaryGroupsEffectivePriceInCents(floorPlanOptions)}
      items={buildSummaryListEffectiveLineItems(floorPlanOptions, true)}
    />
  );
}

type InteriorPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function InteriorPDFCard({ optionGroups, shouldDisplayPricing }: InteriorPDFCardProps) {
  const interiorDesignPackages = optionGroups.filter(
    (ccog) => ccog.step === CheckoutConfigOptionGroupStep.InteriorDesignPackage && hasSelectedOption(ccog),
  );
  const interiorSchemeOptionGroup = optionGroups.find(
    (ccog) => ccog.group.group.type.isInteriorScheme && hasSelectedOption(ccog),
  )!;

  return (
    <div css={Css.df.fdc.$}>
      {interiorDesignPackages.map((ccog) => {
        const isSpecLevel = ccog.group.group.type.isSpecLevel;
        const selectedOptionItem = {
          title: ccog.selectedOption!.option.name,
          optionCode: ccog.selectedOption!.option.code,
        };
        const items = isSpecLevel
          ? [
              selectedOptionItem,
              ...buildSummaryListEffectiveLineItems(getDownstreamIncludedOptionGroups(ccog, optionGroups), true),
              // Add includedBy scheme under spec level
              ...buildSummaryListEffectiveLineItems(
                getDownstreamIncludedOptionGroups(interiorSchemeOptionGroup, optionGroups),
                true,
              ),
            ]
          : [selectedOptionItem];

        return (
          <PrintListItem
            key={ccog.id}
            title={ccog.group.name}
            shouldDisplayPricing={shouldDisplayPricing}
            priceInCents={ccog.selectedOption!.totalPriceInCents}
            items={items}
            shouldIndent={isSpecLevel}
          />
        );
      })}
    </div>
  );
}

type InteriorPersonalizationAndAddOnsPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function InteriorPersonalizationAndAddOnsPDFCard({
  optionGroups,
  shouldDisplayPricing,
}: InteriorPersonalizationAndAddOnsPDFCardProps) {
  const personalizationSteps = [
    CheckoutConfigOptionGroupStep.InteriorPersonalization,
    CheckoutConfigOptionGroupStep.OptionsAddon,
  ];
  const filteredPersonalizationOptions = filterSpecLevelOrInteriorSchemeEnabledOptionGroups(
    optionGroups.filter((ccog) => personalizationSteps.includes(ccog.step) && hasSelectedOption(ccog)),
    optionGroups,
  );

  if (filteredPersonalizationOptions.isEmpty) return null;

  return (
    <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
      <PrintListItem
        title="Options"
        shouldDisplayPricing={shouldDisplayPricing}
        priceInCents={getSummaryGroupsEffectivePriceInCents(filteredPersonalizationOptions)}
        items={buildSummaryListEffectiveLineItems(filteredPersonalizationOptions, true)}
      />
    </div>
  );
}

type AduPDFCardProps = { shouldDisplayPricing: boolean } & Pick<
  SummaryPrintConfiguration_CheckoutConfigFragment,
  "optionGroups"
>;

function AduPDFCard({ optionGroups, shouldDisplayPricing }: AduPDFCardProps) {
  const selectedAduOptions = optionGroups.filter(
    (ccog) => ccog.step === CheckoutConfigOptionGroupStep.Adu && hasSelectedOption(ccog),
  );

  if (selectedAduOptions.isEmpty) return null;

  return (
    <div css={Css.df.fdc.add({ breakInside: "avoid-page" }).$}>
      <PrintListItem
        title="ADU"
        shouldDisplayPricing={shouldDisplayPricing}
        priceInCents={getSummaryGroupsEffectivePriceInCents(selectedAduOptions)}
        items={buildSummaryListEffectiveLineItems(selectedAduOptions)}
      />
    </div>
  );
}

// filter out any option groups that have been included by the spec level or interior scheme
function filterSpecLevelOrInteriorSchemeEnabledOptionGroups(
  targetOptionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  allOptionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
) {
  // Note: Splat since irl impossible to not have a spec level
  const specLevelOptionGroup = allOptionGroups.find(
    (ccog) => ccog.group.group.type.isSpecLevel && hasSelectedOption(ccog),
  )!;
  const interiorSchemeOptionGroup = allOptionGroups.find(
    (ccog) => ccog.group.group.type.isInteriorScheme && hasSelectedOption(ccog),
  )!;
  const downstreamIncludedOptionGroups = [
    ...getDownstreamIncludedOptionGroups(specLevelOptionGroup, allOptionGroups),
    ...getDownstreamIncludedOptionGroups(interiorSchemeOptionGroup, allOptionGroups),
  ];
  return targetOptionGroups.filter((ccog) => !downstreamIncludedOptionGroups.includes(ccog));
}
